<section class="step" aria-labelledby="sb-numbers-title">
  <!-- Who gets a message -->
  <div class="bk-panel panel">
    <header class="panel__head">
      <div class="panel__titles">
        <h2 class="bk-section-title panel__title" id="sb-numbers-title" tabindex="-1">Who gets a message</h2>
        <p class="panel__sub">
          Pick the column with phone numbers. Every row with a usable number gets its own SMS.
        </p>
      </div>
    </header>

    <div class="settings">
      <div class="bk-field">
        <span class="bk-field__label" id="sb-column-label">Phone number column</span>
        <!-- Custom listbox rather than a native <select>: the browser/OS draws the
             native option popup itself, which on Windows in dark mode mixes a light
             popup background with light text and is unreadable. Rendering the list
             ourselves keeps contrast identical across platforms. It follows the
             ARIA select-only combobox pattern, so the active option is announced. -->
        <div class="picker" (clickOutside)="close()">
          <button
            type="button"
            role="combobox"
            class="bk-field__input picker__btn"
            aria-haspopup="listbox"
            aria-labelledby="sb-column-label"
            [attr.aria-expanded]="open"
            [attr.aria-controls]="open ? 'sb-column-list' : null"
            [attr.aria-activedescendant]="open && activeIndex > -1 ? 'sb-column-option-' + activeIndex : null"
            (click)="toggle()"
            (keydown)="onKeydown($event)"
          >
            <span id="sb-column-value" class="picker__value" [class.is-empty]="store.phoneIndex() < 0">
              {{ store.phoneLabel() || "Choose a column" }}
            </span>
            <span class="bk-sym picker__caret" [class.is-open]="open" aria-hidden="true">expand_more</span>
          </button>

          @if (open) {
            <ul #listbox id="sb-column-list" role="listbox" aria-labelledby="sb-column-label" class="picker__list">
              @for (column of store.columns(); track column.index; let i = $index) {
                <li
                  role="option"
                  class="picker__item"
                  [id]="'sb-column-option-' + i"
                  [attr.aria-selected]="i === store.phoneIndex()"
                  [class.is-active]="i === activeIndex"
                  (click)="choose(i)"
                  (mouseenter)="activeIndex = i"
                >
                  <span class="picker__text">
                    <span class="picker__name">{{ column.header || column.field }}</span>
                    @if (column.example) {
                      <span class="picker__example">{{ column.example }}</span>
                    }
                  </span>
                  @if (i === store.phoneIndex()) {
                    <span class="bk-sym picker__check" aria-hidden="true">check</span>
                  }
                </li>
              }
            </ul>
          }
        </div>
        @if (autoShare() && store.phoneIndex() >= 0) {
          <p class="bk-field__hint">Picked for you: {{ autoShare() }} of its values read as phone numbers.</p>
        } @else if (store.phoneIndex() < 0) {
          <p class="bk-field__hint">No column looked like phone numbers. Choose the one that holds them.</p>
        }
      </div>

      <div class="bk-field">
        <label class="bk-field__label" for="sb-country">Local numbers are in</label>
        <select id="sb-country" class="bk-field__input" (change)="onCountry($event)">
          <option value="" [selected]="!store.country()">No default country</option>
          @for (country of countries; track country.code) {
            <option [value]="country.code" [selected]="country.code === store.country()">
              {{ country.name }} ({{ country.dial }})
            </option>
          }
        </select>
        <p class="bk-field__hint">
          @if (countryName()) {
            Numbers without a country code, like 071 234 567, are read as {{ countryName() }} numbers.
          } @else {
            Every number needs its country code, like +267 71 234 567.
          }
        </p>
      </div>
    </div>

    @if (store.phoneIndex() >= 0) {
      <dl class="figures" aria-label="Phone numbers">
        <div class="figure">
          <dt class="figure__label">Will get a message</dt>
          <dd class="figure__value">{{ formatCount(stats().send) }}</dd>
        </div>
        <div class="figure" [attr.data-tone]="stats().invalid ? 'bad' : null">
          <dt class="figure__label">Invalid</dt>
          <dd class="figure__value">{{ formatCount(stats().invalid) }}</dd>
        </div>
        <div class="figure">
          <dt class="figure__label">Repeated</dt>
          <dd class="figure__value">{{ formatCount(stats().repeats) }}</dd>
        </div>
        <div class="figure">
          <dt class="figure__label">No number</dt>
          <dd class="figure__value">{{ formatCount(stats().empty) }}</dd>
        </div>
      </dl>

      <fieldset class="clean">
        <legend class="bk-eyebrow clean__legend">Clean the list before it goes out</legend>
        <div class="clean__option">
          <label class="bk-check">
            <input
              type="checkbox"
              aria-describedby="sb-drop-hint"
              [checked]="store.dropInvalid()"
              (change)="onDropInvalid($event)"
            />
            Leave out invalid numbers
            @if (stats().invalid) {
              <span class="clean__count">· {{ formatCount(stats().invalid) }}</span>
            }
          </label>
          <p class="clean__hint" id="sb-drop-hint">They can’t be delivered.</p>
        </div>
        <div class="clean__option">
          <label class="bk-check">
            <input
              type="checkbox"
              aria-describedby="sb-dedupe-hint"
              [checked]="store.dedupe()"
              (change)="onDedupe($event)"
            />
            Send each number only once
            @if (stats().repeats) {
              <span class="clean__count">· {{ formatCount(stats().repeats) }}</span>
            }
          </label>
          <p class="clean__hint" id="sb-dedupe-hint">
            Keeps the first row for each number. Turn this off if someone should get one message per row.
          </p>
        </div>
        <p class="clean__hint">Rows without a number are always skipped.</p>
      </fieldset>

      @if (phoneShortened()) {
        <ul class="notes">
          <li class="note" data-tone="bad">
            <span>
              {{ formatCount(phoneShortened()) }} {{ phoneShortened() === 1 ? "number was" : "numbers were" }}
              shortened by Excel, like 2.67E+11, and can’t be sent.
              <span class="note__more">
                Format the column as Text in Excel, re-enter the numbers, then upload the sheet again.
              </span>
            </span>
          </li>
        </ul>
      }

      @if (!stats().send) {
        <p class="sb-error" role="alert">
          <span class="bk-sym" aria-hidden="true">error</span>
          <span>
            No row has a usable number in “{{ store.phoneLabel() }}”. Choose another column, or set the country
            for local numbers.
          </span>
        </p>
      }
    }
  </div>

  <!-- The first rows, as written and as sent -->
  @if (store.phoneIndex() >= 0) {
    <div class="bk-panel panel panel--flush">
      <header class="panel__head">
        <div class="panel__titles">
          <h2 class="bk-section-title panel__title" id="sb-sample-title">First rows</h2>
          <p class="panel__sub">How the first numbers read. Review lists every row worth a look.</p>
        </div>
      </header>
      <!-- Focusable so keyboard users can scroll a table wider than the panel. -->
      <div class="table-wrap" tabindex="0" role="region" aria-labelledby="sb-sample-title">
        <table class="bk-log sample">
          <thead>
            <tr>
              <th scope="col" class="num">Row</th>
              <th scope="col">In the sheet</th>
              <th scope="col" class="col-sends">Sends to</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            @for (row of sample(); track row.sheetRow) {
              <tr>
                <td class="num quiet">{{ row.sheetRow }}</td>
                <td><span class="cell-clip">{{ display(row.raw) }}</span></td>
                <td class="col-sends">{{ row.sendsTo }}</td>
                <td><app-status [text]="row.label" [status]="row.tone" /></td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }

  <!-- Every column and the field it becomes -->
  <div class="bk-panel panel panel--flush">
    <header class="panel__head">
      <div class="panel__titles">
        <h2 class="bk-section-title panel__title" id="sb-fields-title">Fields for your message</h2>
        <p class="panel__sub">
          Each column can go into the message under its field name. You’ll insert them on the next step.
        </p>
      </div>
    </header>
    <div class="table-wrap" tabindex="0" role="region" aria-labelledby="sb-fields-title">
      <table class="bk-log fields">
        <thead>
          <tr>
            <th scope="col">Column</th>
            <th scope="col">Field</th>
            <th scope="col" class="col-example">Example</th>
            <th scope="col" class="num col-filled">Filled in</th>
          </tr>
        </thead>
        <tbody>
          @for (column of store.columns(); track column.index) {
            <tr>
              <td class="col-name">
                <span class="cell-clip">{{ column.header || "No name" }}</span>
                @if (column.index === store.phoneIndex()) {
                  <span class="fields__note">Phone number column</span>
                } @else if (column.renamed) {
                  <span class="fields__note">{{ renameNote(column.renamed) }}</span>
                }
              </td>
              <td class="col-field"><span class="token">{{ token(column.field) }}</span></td>
              <td class="quiet col-example"><span class="cell-clip">{{ column.example || "—" }}</span></td>
              <td class="num quiet col-filled">{{ filledLabel(column.filled) }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  <footer class="step__foot">
    <button type="button" class="bk-btn bk-btn--ghost" (click)="back.emit()">
      <span class="bk-sym" aria-hidden="true">arrow_back</span>
      Back
    </button>
    <div class="step__foot-end">
      @if (!canContinue) {
        <span class="step__why" id="sb-numbers-why">
          {{ store.phoneIndex() >= 0 ? "No usable numbers yet." : "Choose the phone number column first." }}
        </span>
      }
      <button
        type="button"
        class="bk-btn bk-btn--primary"
        [attr.aria-disabled]="!canContinue || null"
        [attr.aria-describedby]="canContinue ? null : 'sb-numbers-why'"
        (click)="continue()"
      >
        Write the message
        <span class="bk-sym trail" aria-hidden="true">arrow_forward</span>
      </button>
    </div>
  </footer>
</section>
